import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import './App.css';
import PoseDetector from './components/PoseDetector';
import PoseSequenceDisplay from './components/PoseSequenceDisplay';
import SequenceComplete from './components/SequenceComplete';
import api from './utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

interface PoseInfo {
  pose: string;
  score: number;
  feedback: string;
}

function YogaApp() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isDetecting, setIsDetecting] = useState(false);
  const [currentPoseInfo, setCurrentPoseInfo] = useState<PoseInfo | null>(null);
  const [isSequenceComplete, setIsSequenceComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseData, setCourseData] = useState<any>(null);

  // Fetch course data using cookie-based authentication
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) {
        setError('No course ID provided in URL');
        setIsLoading(false);
        return;
      }
      
      try {
        console.log(`Fetching course data for courseId: ${courseId}`);
        console.log(`Practice session ID: ${sessionId}`);
        
        // Cookie-based authentication happens automatically with withCredentials: true
        // Get course data from main app
        const data = await api.courses.getCoursePlan(courseId);
        
        console.log('Raw API response data:', data);
        console.log('Response data type:', typeof data);
        console.log('Response has course_plan property:', data && data.hasOwnProperty('course_plan'));
        console.log('Response keys:', data ? Object.keys(data) : 'No data');
        
        if (!data) {
          throw new Error('Invalid course data: No data received from API');
        }
        
        if (!data.course_plan) {
          console.error('Data structure without course_plan:', JSON.stringify(data, null, 2));
          throw new Error('Invalid course data: Missing course_plan property');
        }
        
        console.log('Retrieved course_plan:', data.course_plan);
        
        // The API response likely has a nested structure
        // Check if the AI instructions are nested inside course_plan
        let courseDataToUse;
        if (data.course_plan.ai_instruction) {
          console.log('Using AI instruction data from course_plan');
          courseDataToUse = data.course_plan.ai_instruction;
        } else {
          console.log('Using course_plan data directly');
          courseDataToUse = data.course_plan;
        }
        
        console.log('Final course data structure:', courseDataToUse);
        console.log('Final course data has sequences:', Boolean(courseDataToUse.sequences));
        if (courseDataToUse.sequences) {
          console.log('Number of sequences:', courseDataToUse.sequences.length);
        }
        
        // Set the course data
        setCourseData(courseDataToUse);
        
        setIsLoading(false);
      } catch (err: any) {
        console.error('Error fetching course data:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load course data');
        setIsLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, sessionId]);

  const handleDetectionChange = (info: PoseInfo) => {
    // Update detection state based on pose info
    setIsDetecting(info.pose !== 'Sequence completed');
    setCurrentPoseInfo(info);
    
    // Check if sequence is complete
    if (info.pose === 'Sequence completed') {
      setIsSequenceComplete(true);
    }
  };

  const handleStartNewSequence = () => {
    setIsSequenceComplete(false);
    setIsDetecting(false);
    setCurrentPoseInfo(null);
  };

  if (isLoading) {
    return <div className="loading-container">Loading course data...</div>;
  }

  if (error) {
    return <div className="error-container">Error: {error}</div>;
  }

  return (
    <div className="yoga-app">
      <PoseDetector 
        apiEndpoint={BACKEND_URL}
        onDetectionChange={handleDetectionChange}
        initialCourseData={courseData}
      />
      {!isSequenceComplete && (
        <PoseSequenceDisplay 
          isDetecting={isDetecting} 
          sequenceId="1"
        />
      )}
      {isSequenceComplete && (
        <SequenceComplete onStartNewSequence={handleStartNewSequence} />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/course/:courseId" element={<YogaApp />} />
        <Route path="/" element={<div>Please navigate to /course/{'{courseId}'}?session_id={'{sessionId}'}</div>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
